import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";

/* import utils */
import { filterString } from "./../../../util/functions";
import { NotifyContext } from "./../../../util/notify";

/* import services */
import api from "./../../../services/api";

/* import components */
import Search from "./../../../components/Search";
import Legend from "./../../../components/Legend";

/* import icons */
import EditIcon from "./../../../assets/icons/edit";

/* import styles */
import {
  Container,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  ActionButton,
  Modal,
  ModalContent,
  Form,
  FormGroup,
  Label,
  Input,
  ButtonGroup,
  Content,
  TableBody,
  AddButton,
  CheckboxLabel,
  HelperText,
  DangerActionButton,
} from "./styles";

export default function Subscribers({ toggleTitles }) {
  const { course_id } = useParams();
  const { notify } = useContext(NotifyContext);

  const [searchValue, setSearchValue] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [data, setData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [course, setCourse] = useState({});
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    cpf: "",
    email: "",
    tel: "",
    password: "",
    present: false,
    send_email: true,
    user_exists: false,
  });

  const handleFilter = async text => {
    const filtered = initialData.filter(
      item =>
        filterString(item?.site_user?.name || "", text) ||
        filterString(item.site_user?.cpf || "", text) ||
        filterString(item.site_user?.email || "", text) ||
        filterString(item.site_user?.tel || "", text),
    );
    setData(filtered);
  };

  const handleStatusFilter = status => {
    setSearchStatus(status);
    let filtered = initialData;

    if (status === "present") {
      filtered = initialData.filter(item => item.present === true);
    } else if (status === "absent") {
      filtered = initialData.filter(item => item.present === false);
    } else {
      filtered = initialData;
    }

    if (searchValue.trim() !== "") {
      filtered = filtered.filter(
        item =>
          filterString(item?.site_user?.name || "", searchValue) ||
          filterString(item?.site_user?.cpf || "", searchValue) ||
          filterString(item?.site_user?.email || "", searchValue) ||
          filterString(item?.site_user?.tel || "", searchValue),
      );
    }

    setData(filtered);
  };

  const formatCPF = cpf => {
    if (!cpf) return "";
    const digits = String(cpf).replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.replace(/(\d{3})(\d+)/, "$1.$2");
    if (digits.length <= 9) return digits.replace(/(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  // React 16 reutiliza o evento sintético — ler o valor ANTES de qualquer setState
  const handleAddFieldChange = field => e => {
    const value = e.target ? e.target.value : "";
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCpfChange = e => {
    const value = e.target ? e.target.value : "";
    const formatted = formatCPF(value);
    setAddForm(prev => ({
      ...prev,
      cpf: formatted,
      user_exists: false,
    }));
  };

  const handleAddCpfBlur = e => {
    const value = e.target ? e.target.value : "";
    setAddForm(prev => ({ ...prev, cpf: value }));
    lookupUserByCpf(value);
  };

  const handleAddCheckboxChange = field => e => {
    const checked = e.target ? e.target.checked : false;
    setAddForm(prev => ({ ...prev, [field]: checked }));
  };

  const handleEditCpfChange = e => {
    if (!e || !e.target) return;
    e.target.value = formatCPF(e.target.value);
  };

  async function loadData() {
    notify.show(true, "Carregando", "info");
    try {
      const { data } = await api.get(`/courses/${course_id}/subscribes`);
      setCourse(data);
      setInitialData(data.course_subscribes);
      setData(data.course_subscribes);
      toggleTitles("Cursos", `Gerenciar Inscritos - ${data.name}`);
    } catch (error) {
      console.log(error);
      notify.show(true, "Erro ao carregar inscritos", "error");
    }
    notify.show(false, "Carregando", "info");
  }

  const handleEdit = subscriber => {
    setSelectedSubscriber(subscriber);
    setShowModal(true);
  };

  const resetAddForm = () => {
    setAddForm({
      name: "",
      cpf: "",
      email: "",
      tel: "",
      password: "",
      present: false,
      send_email: true,
      user_exists: false,
    });
  };

  const openAddModal = () => {
    resetAddForm();
    setShowAddModal(true);
  };

  const lookupUserByCpf = async cpfValue => {
    const digits = String(cpfValue || "").replace(/\D/g, "");
    if (digits.length !== 11) return;

    setLookingUp(true);
    try {
      const { data: user } = await api.get(`/site_users/cpf/${digits}`);
      setAddForm(prev => ({
        ...prev,
        cpf: formatCPF(digits),
        name: user.name || "",
        email: user.email || "",
        tel: user.tel || "",
        user_exists: true,
      }));
      notify.show(true, "Profissional encontrado. Dados preenchidos.", "success");
    } catch (error) {
      setAddForm(prev => ({
        ...prev,
        cpf: formatCPF(digits),
        user_exists: false,
      }));
    } finally {
      setLookingUp(false);
    }
  };

  const handleAddSave = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const cpf = addForm.cpf.replace(/\D/g, "");
      if (cpf.length !== 11) {
        notify.show(true, "CPF deve ter 11 dígitos", "error");
        setLoading(false);
        return;
      }

      if (!addForm.name.trim()) {
        notify.show(true, "Nome é obrigatório", "error");
        setLoading(false);
        return;
      }

      if (addForm.email && addForm.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(addForm.email)) {
          notify.show(true, "Email inválido", "error");
          setLoading(false);
          return;
        }
      }

      const payload = {
        name: addForm.name.trim(),
        cpf: formatCPF(cpf),
        email: addForm.email || null,
        tel: addForm.tel || null,
        present: !!addForm.present,
        send_email: !!addForm.send_email,
      };

      if (addForm.password && addForm.password.trim() !== "") {
        if (addForm.password.length < 6) {
          notify.show(true, "A senha deve ter pelo menos 6 caracteres", "error");
          setLoading(false);
          return;
        }
        payload.password = addForm.password;
      }

      const { data: result } = await api.post(`/courses/${course_id}/subscribe/admin`, payload);

      let message = "Inscrito adicionado com sucesso";
      if (result.created_user && result.temporary_password) {
        message = `Inscrito criado. Senha temporária: ${result.temporary_password}`;
      }

      notify.show(true, message, "success");
      setShowAddModal(false);
      resetAddForm();
      loadData();
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 409) {
        notify.show(true, "Este profissional já está inscrito neste curso", "error");
      } else {
        notify.show(true, "Erro ao adicionar inscrito", "error");
      }
    }

    setLoading(false);
  };

  const handleSave = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const updateData = {
        name: formData.get("name"),
        cpf: formData.get("cpf"),
        email: formData.get("email"),
        address_cep: formData.get("address_cep"),
        address: formData.get("address"),
        address_city: formData.get("address_city"),
        address_state: formData.get("address_state"),
        tel: formData.get("tel"),
        is_active: formData.get("is_active") === "true",
      };

      const cpf = updateData.cpf.replace(/\D/g, "");
      if (cpf.length !== 11) {
        notify.show(true, "CPF deve ter 11 dígitos", "error");
        setLoading(false);
        return;
      }

      if (updateData.email && updateData.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(updateData.email)) {
          notify.show(true, "Email inválido", "error");
          setLoading(false);
          return;
        }
      }

      const password = formData.get("password");
      const confirmPassword = formData.get("confirmPassword");

      if (password && password.trim() !== "") {
        if (password !== confirmPassword) {
          notify.show(true, "As senhas não coincidem", "error");
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          notify.show(true, "A senha deve ter pelo menos 6 caracteres", "error");
          setLoading(false);
          return;
        }
        updateData.password = password;
      } else if (confirmPassword && confirmPassword.trim() !== "") {
        notify.show(true, "Preencha a senha antes de confirmá-la", "error");
        setLoading(false);
        return;
      }

      await api.put(`/site_users/${selectedSubscriber.site_user.id}`, updateData);

      notify.show(true, "Inscrito atualizado com sucesso", "success");
      setShowModal(false);
      setSelectedSubscriber(null);
      loadData();
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 409) {
        notify.show(true, "CPF já cadastrado para outro usuário", "error");
      } else {
        notify.show(true, "Erro ao atualizar inscrito", "error");
      }
    }

    setLoading(false);
  };

  async function setPresent(site_user_id, present) {
    try {
      await api.post(`/courses/${course_id}/${site_user_id}/present/${present}`, {});
      notify.show(true, "Presença atualizada", "success");
      loadData();
    } catch (error) {
      notify.show(true, "Erro ao definir presença! Tente novamente", "error");
      console.log(error);
      loadData();
    }
  }

  const handleRemove = async subscriber => {
    const name = subscriber?.site_user?.name || "este inscrito";
    if (!window.confirm(`Remover a inscrição de "${name}" deste curso?\n\nA conta do usuário no site será mantida.`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/courses/${course_id}/subscribes/${subscriber.id}`);
      notify.show(true, "Inscrição removida com sucesso", "success");
      setShowModal(false);
      setSelectedSubscriber(null);
      loadData();
    } catch (error) {
      console.log(error);
      notify.show(true, "Erro ao remover inscrição", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    handleFilter(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (initialData.length > 0) {
      handleStatusFilter(searchStatus);
    }
  }, [searchStatus, initialData]);

  return (
    <>
      <Container>
        <Content>
          <Legend
            active={searchStatus}
            onClick={status => {
              handleStatusFilter(status);
            }}
            items={[
              {
                key: "status",
                value: "",
                title: "Todos",
                color: "#6c757d",
              },
              {
                key: "status",
                value: "present",
                title: "Filtrar Presentes",
                color: "#28a745",
              },
              {
                key: "status",
                value: "absent",
                title: "Filtrar Ausentes",
                color: "#dc3545",
              },
            ]}
          />

          <AddButton type="button" onClick={openAddModal}>
            + Adicionar inscrito
          </AddButton>

          {data.length === 0 && <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>Nenhum inscrito encontrado</div>}

          {data.length > 0 && (
            <div
              style={{
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <div>
                <strong>Total de inscritos: {initialData.length}</strong>
              </div>
              <div style={{ display: "flex", gap: "20px" }}>
                <span style={{ color: "#28a745", fontWeight: "bold" }}>Presentes: {initialData.filter(item => item.present).length}</span>
                <span style={{ color: "#dc3545", fontWeight: "bold" }}>Ausentes: {initialData.filter(item => !item.present).length}</span>
              </div>
            </div>
          )}

          <Search
            containerStyle={{ position: "relative", right: "0", top: "0" }}
            InitialValue={searchValue}
            onChange={text => {
              setSearchValue(text);
            }}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Presente</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(subscriber => (
                <TableRow key={subscriber.id}>
                  <TableCell>{subscriber?.site_user?.name || "-"}</TableCell>
                  <TableCell>{subscriber?.site_user?.cpf || "-"}</TableCell>
                  <TableCell>{subscriber?.site_user?.email || "-"}</TableCell>
                  <TableCell>{subscriber?.site_user?.tel || "-"}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={subscriber.present}
                        onChange={event => {
                          if (!subscriber?.site_user?.id) return;
                          const checked = event.target ? event.target.checked : false;
                          setPresent(subscriber.site_user.id, checked);
                        }}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }}
                      />
                      <span
                        style={{
                          color: subscriber.present ? "green" : "red",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}>
                        {subscriber.present ? "PRESENTE" : "AUSENTE"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <ActionButton onClick={() => handleEdit(subscriber)} title="Editar inscrito">
                        <EditIcon />
                      </ActionButton>
                      <DangerActionButton type="button" onClick={() => handleRemove(subscriber)} title="Remover inscrição">
                        Remover
                      </DangerActionButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Content>
      </Container>

      {showAddModal && (
        <Modal>
          <ModalContent>
            <h2>Adicionar inscrito</h2>
            <Form onSubmit={handleAddSave} autoComplete="off">
              <FormGroup>
                <Label htmlFor="add-subscriber-cpf">CPF:</Label>
                <Input
                  id="add-subscriber-cpf"
                  name="cpf"
                  value={addForm.cpf}
                  onChange={handleAddCpfChange}
                  onBlur={handleAddCpfBlur}
                  required
                />
                <HelperText>{lookingUp ? "Buscando profissional..." : addForm.user_exists ? "Usuário já cadastrado no site" : "Informe o CPF e os demais dados"}</HelperText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="add-subscriber-name">Nome:</Label>
                <Input
                  id="add-subscriber-name"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddFieldChange("name")}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="add-subscriber-email">Email:</Label>
                <Input
                  id="add-subscriber-email"
                  name="email"
                  type="email"
                  value={addForm.email}
                  onChange={handleAddFieldChange("email")}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="add-subscriber-tel">Telefone:</Label>
                <Input id="add-subscriber-tel" name="tel" value={addForm.tel} onChange={handleAddFieldChange("tel")} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="add-subscriber-password">
                  Senha {addForm.user_exists ? "(opcional)" : "(opcional — gerada automaticamente se vazia)"}:
                </Label>
                <Input
                  id="add-subscriber-password"
                  name="password"
                  type="password"
                  value={addForm.password}
                  onChange={handleAddFieldChange("password")}
                  placeholder="Mínimo 6 caracteres"
                />
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <input type="checkbox" checked={addForm.present} onChange={handleAddCheckboxChange("present")} />
                  Marcar como presente (libera emissão de certificado)
                </CheckboxLabel>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <input type="checkbox" checked={addForm.send_email} onChange={handleAddCheckboxChange("send_email")} />
                  Enviar e-mail de confirmação
                </CheckboxLabel>
              </FormGroup>

              <ButtonGroup>
                <button type="button" onClick={() => setShowAddModal(false)} data-variant="cancel">
                  Cancelar
                </button>
                <button type="submit" disabled={loading || lookingUp} data-variant="primary">
                  {loading ? "Salvando..." : "Adicionar"}
                </button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {showModal && selectedSubscriber && (
        <Modal>
          <ModalContent>
            <h2>Editar Inscrito</h2>
            <Form onSubmit={handleSave}>
              <FormGroup>
                <Label>Nome:</Label>
                <Input name="name" defaultValue={selectedSubscriber.site_user.name} required />
              </FormGroup>

              <FormGroup>
                <Label>CPF:</Label>
                <Input name="cpf" defaultValue={formatCPF(selectedSubscriber.site_user.cpf)} onChange={handleEditCpfChange} required />
              </FormGroup>

              <FormGroup>
                <Label>Email:</Label>
                <Input name="email" type="email" defaultValue={selectedSubscriber.site_user.email} />
              </FormGroup>

              <FormGroup>
                <Label>Senha (deixe em branco para não alterar):</Label>
                <Input name="password" type="password" placeholder="Nova senha (opcional)" />
              </FormGroup>

              <FormGroup>
                <Label>Confirmar Senha:</Label>
                <Input name="confirmPassword" type="password" placeholder="Confirme a nova senha" />
              </FormGroup>

              <FormGroup>
                <Label>Telefone:</Label>
                <Input name="tel" defaultValue={selectedSubscriber.site_user.tel} />
              </FormGroup>

              <FormGroup>
                <Label>CEP:</Label>
                <Input name="address_cep" defaultValue={selectedSubscriber.site_user.address_cep} />
              </FormGroup>

              <FormGroup>
                <Label>Endereço:</Label>
                <Input name="address" defaultValue={selectedSubscriber.site_user.address} />
              </FormGroup>

              <FormGroup>
                <Label>Cidade:</Label>
                <Input name="address_city" defaultValue={selectedSubscriber.site_user.address_city} />
              </FormGroup>

              <FormGroup>
                <Label>Estado:</Label>
                <Input name="address_state" defaultValue={selectedSubscriber.site_user.address_state} />
              </FormGroup>

              <ButtonGroup>
                <button type="button" data-variant="danger" disabled={loading} onClick={() => handleRemove(selectedSubscriber)}>
                  Remover inscrição
                </button>
                <button type="button" data-variant="cancel" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" data-variant="primary" disabled={loading}>
                  {loading ? "Salvando..." : "Salvar"}
                </button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
